import { colors } from "@permisso/permisso-components";
import { create } from "zustand";

export type Theme = {
  companyName: string;
  companyLogo: string;
  primaryColor: string;
  linkColor: string;
  visitedLinkColor: string;
  blackColor: string;
  errorColor: string;
  successColor: string;
  tabBarColor: string;
  fontFamily: string;
  fontFamilyHeadings: string;
  fontSize: number;
  fontLinks: Array<string>;
  removeHeader: boolean;
  removeCustomerLogo: boolean;
  removePoweredBy: boolean;
  primaryButtonBackgroundColor: string | undefined;
  primaryButtonTextColor: string | undefined;
  primaryButtonDisabledBackgroundColor: string | undefined;
  primaryButtonDisabledTextColor: string | undefined;
};

const themeStore = create<Theme & { setTheme: (value: Partial<Theme>) => void }>((set) => ({
  companyName: "Permisso",
  companyLogo: "https://cdn.permisso.io/widget/common/permisso-icon-blue-background-logo.svg",
  primaryColor: colors.brandMainColor,
  linkColor: colors.grey700,
  visitedLinkColor: colors.grey700,
  blackColor: colors.grey800,
  errorColor: colors.darkRed,
  successColor: colors.darkGreen,
  tabBarColor: colors.brandMainColor,
  fontFamily: "'Roboto', sans-serif",
  fontFamilyHeadings: "'Roboto', sans-serif",
  fontSize: 16,
  fontLinks: ["https://fonts.googleapis.com/css?family=Roboto:400,500,700"],
  removeHeader: false,
  removeCustomerLogo: false,
  removePoweredBy: false,
  primaryButtonBackgroundColor: undefined,
  primaryButtonTextColor: undefined,
  primaryButtonDisabledBackgroundColor: undefined,
  primaryButtonDisabledTextColor: undefined,
  setTheme: ({
    companyName,
    companyLogo,
    primaryColor,
    linkColor,
    visitedLinkColor,
    blackColor,
    errorColor,
    successColor,
    tabBarColor,
    fontFamily,
    fontFamilyHeadings,
    fontSize,
    fontLinks,
    removeHeader,
    removeCustomerLogo,
    removePoweredBy,
    primaryButtonBackgroundColor,
    primaryButtonTextColor,
    primaryButtonDisabledBackgroundColor,
    primaryButtonDisabledTextColor,
  }) => {
    const toSet: Partial<Theme> = {};
    if (companyName) toSet.companyName = companyName;
    if (companyLogo) toSet.companyLogo = companyLogo;
    if (primaryColor) toSet.primaryColor = primaryColor;
    if (linkColor) toSet.linkColor = linkColor;
    if (visitedLinkColor ?? linkColor) toSet.visitedLinkColor = visitedLinkColor ?? linkColor;
    if (blackColor) toSet.blackColor = blackColor;
    if (errorColor) toSet.errorColor = errorColor;
    if (successColor) toSet.successColor = successColor;
    if (tabBarColor ?? primaryColor) toSet.tabBarColor = tabBarColor ?? primaryColor;
    if (fontFamily) {
      toSet.fontFamily = fontFamily;
      toSet.fontFamilyHeadings = fontFamily;
    }
    if (fontFamilyHeadings) toSet.fontFamilyHeadings = fontFamilyHeadings;
    if (fontSize) toSet.fontSize = fontSize;
    if (fontLinks && fontLinks.length > 0) toSet.fontLinks = fontLinks;
    if (removeHeader !== undefined) toSet.removeHeader = removeHeader;
    if (removeCustomerLogo !== undefined) toSet.removeCustomerLogo = removeCustomerLogo;
    if (removePoweredBy !== undefined) toSet.removePoweredBy = removePoweredBy;
    if (removePoweredBy !== undefined) toSet.removePoweredBy = removePoweredBy;
    if (primaryButtonBackgroundColor)
      toSet.primaryButtonBackgroundColor = primaryButtonBackgroundColor;
    if (primaryButtonTextColor) toSet.primaryButtonTextColor = primaryButtonTextColor;
    if (primaryButtonDisabledBackgroundColor)
      toSet.primaryButtonDisabledBackgroundColor = primaryButtonDisabledBackgroundColor;
    if (primaryButtonDisabledTextColor)
      toSet.primaryButtonDisabledTextColor = primaryButtonDisabledTextColor;
    set(toSet);
  },
}));

export const useTheme = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return themeStore(({ setTheme, ...rest }) => rest);
};

export const useSetTheme = () => {
  return themeStore(({ setTheme }) => setTheme);
};
